import { ClearableInput } from "@/components/atoms/clearableInput";
import { useEffect, useRef, useState } from "react";
import { useStyle } from "./styles";
import { useIntl } from "react-intl";
import { Search24Regular } from "@fluentui/react-icons";
import { ContactSearchProps } from "./types";
import { Avatar, mergeClasses } from "@fluentui/react-components";
import { useDebounce } from "@/common/helpers/useDebounce";
import { ContactListItem, ShortContactInfo } from "@/repository/models";
import { searchContactsByName } from "@/repository/queryFns";
import { ListItemProps } from "@/components/atoms/list";
import { SuggestionsPopover } from "../suggestionsPopover";

export const ContactSearch = ({
	classesNames,
	onItemSelected,
}: ContactSearchProps) => {
	const intl = useIntl();
	const classes = useStyle();

	const inputRef = useRef<HTMLInputElement>(null);
	const [searchText, setSearchText] = useState<string>("");
	const [listItems, setListItems] = useState<ListItemProps[]>([]);
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);

	useEffect(() => setIsPopoverOpen(!!listItems.length), [listItems]);

	useEffect(() => {
		if (!searchText) {
			setListItems([]);
			return;
		}

		executeQuery(searchText);
	}, [searchText]);

	const [selectItemCallback] = useDebounce((item: ShortContactInfo) => {
		onItemSelected?.(item);
		setSearchText("");
		setListItems([]);
		setIsPopoverOpen(false);
	}, 150);

	const queryFn = (name: string) =>
		searchContactsByName(name).then((x: ContactListItem[]) =>
			x.map((s) => ({
				key: s.id,
				header: s.name,
				content: [s.jobTitle, s.department].filter((x) => !!x).join("/"),
				data: s,
			}))
		);

	const [executeQuery] = useDebounce((searchValue: string) => {
		if (!searchValue) {
			return;
		}

		queryFn(searchValue)
			.then((x) =>
				setListItems(
					x.map((a) => ({
						key: a.key,
						header: a.header,
						content: a.content,
						media: <Avatar name={a.data.name} />,
						onClick: () => selectItemCallback(a.data),
					}))
				)
			)
			.catch(() => setListItems([]));
	}, 500);

	return (
		<>
			<ClearableInput
				ref={inputRef}
				clearable
				value={searchText}
				className={mergeClasses(classes.searchInput, classesNames?.searchInput)}
				placeholder={intl.formatMessage({ id: "Contacts.SearchPlaceholder" })}
				contentAfter={<Search24Regular />}
				onChange={setSearchText}
			/>
			{inputRef.current && (
				<SuggestionsPopover
					target={inputRef.current}
					open={isPopoverOpen}
					items={listItems}
					onDismiss={() => setIsPopoverOpen(false)}
					positioning={{
						align: "start",
						offset: { crossAxis: -10, mainAxis: 5 },
					}}
					size={"large"}
					classesName={{
						surface: classes.popoverSurface,
						list: {
							containerHeight: classes.listContainer,
							item: classes.listItem,
						},
					}}
				/>
			)}
		</>
	);
};
